import { useEffect } from 'react';

import { subscribeWindowClass } from './useWindowClass';

/**
 * THE ITEMS TOOLBAR IS THE SECOND PIN (KPR-1724). In medium and expanded windows
 * (platform-standards §18) the items page's two-row toolbar sticks beneath the
 * pinned shell header; in compact it scrolls away with it (a phone pins nothing, docs/platform-standards.md §13.2).
 * Everything else that sticks on the page (the table's column header, the date
 * group headers, the detail pane) and the document's scroll padding sit beneath
 * it, so while it really is pinned this publishes its height on <html>:
 *
 *   --list-toolbar-height   added to --shell-pin-offset by every sticky layer
 *                           below the toolbar
 *
 * "Really pinned" is read from the computed position, as useShellPin does: the
 * stylesheet decides and this follows it. The variable is removed when the
 * toolbar is not sticky and on unmount, so no other route moves.
 */
export const LIST_TOOLBAR_VAR = '--list-toolbar-height';

export default function useListToolbarPin(ref) {
	useEffect(() => {
		const toolbar = ref.current;
		if (!toolbar || typeof window === 'undefined') return undefined;
		const root = document.documentElement;

		const clear = () => root.style.removeProperty(LIST_TOOLBAR_VAR);
		const publish = () => {
			if (window.getComputedStyle(toolbar).position !== 'sticky') {
				clear();
				return;
			}
			const height = Math.round(toolbar.getBoundingClientRect().height);
			root.style.setProperty(LIST_TOOLBAR_VAR, `${height}px`);
		};

		publish();
		// Its height changes with its content (chips wrapping onto a second line,
		// a filter applied or cleared); the pin itself with the window class.
		window.addEventListener('resize', publish);
		const stopClass = subscribeWindowClass(publish);
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
		observer?.observe(toolbar);

		return () => {
			window.removeEventListener('resize', publish);
			stopClass();
			observer?.disconnect();
			clear();
		};
	}, [ref]);
}
